import { useMemo, useState } from 'react';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { useAuditLogs } from '@/hooks/useAudit';
import { useUsers } from '@/hooks/useUsers';
import { formatDateTime } from '@/utils/format';

// Etiquetas legibles de cada acción
const ACTION_LABELS: Record<string, string> = {
  login_success: 'Ingreso',
  login_failed: 'Ingreso fallido',
  logout: 'Salida',
  password_changed: 'Cambió contraseña',
  password_reset: 'Reset de contraseña',
  user_created: 'Alta de usuario',
  user_updated: 'Editó usuario',
  user_deleted: 'Baja de usuario',
  user_deactivated: 'Desactivó usuario',
  tenant_created: 'Alta de local',
  tenant_updated: 'Editó local',
  tenant_impersonate: 'Impersonación',
  sale_created: 'Venta',
  sale_cancelled: 'Canceló venta',
  stock_adjusted: 'Ajuste de stock',
  price_bulk_updated: 'Cambio de precios (masivo)',
  product_created: 'Alta de producto',
  product_updated: 'Editó producto',
  product_deleted: 'Baja de producto',
  purchase_order_created: 'Orden de compra',
  purchase_order_received: 'Recepción de compra',
  purchase_order_deleted: 'Borró orden de compra',
  supplier_payment_deleted: 'Borró pago a proveedor',
  data_exported: 'Exportó datos',
  bulk_delete: 'Borrado masivo',
};

// Color del badge según el tipo de acción
function actionColor(action: string): string {
  if (action.includes('delete') || action.includes('cancel') || action === 'login_failed') return 'bg-red-100 text-red-700';
  if (action.includes('created') || action === 'sale_created') return 'bg-emerald-100 text-emerald-700';
  if (action.includes('updated') || action.includes('adjust') || action.includes('price') || action.includes('received')) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

const ENTITY_OPTIONS = [
  { value: '', label: 'Todo' },
  { value: 'product', label: 'Productos' },
  { value: 'sale', label: 'Ventas' },
  { value: 'purchase_order', label: 'Compras' },
  { value: 'user', label: 'Usuarios' },
  { value: 'supplier_payment', label: 'Pagos a proveedor' },
];

function renderDetails(details?: string | null): string {
  if (!details) return '';
  try {
    const obj = JSON.parse(details);
    if (obj && typeof obj === 'object') {
      return Object.entries(obj)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? (v as unknown[]).join(', ') : v}`)
        .join(' · ');
    }
    return String(details);
  } catch {
    return String(details);
  }
}

export default function Audit() {
  const [userId, setUserId] = useState<string>('');
  const [entityType, setEntityType] = useState<string>('');
  const [days, setDays] = useState<number>(30);

  const { data: users } = useUsers(true);
  const { data: logs, isLoading, refetch, isFetching } = useAuditLogs({
    user_id: userId ? Number(userId) : undefined,
    entity_type: entityType || undefined,
    days,
    limit: 500,
  });

  const userName = useMemo(() => {
    const m = new Map<number, string>();
    (users || []).forEach((u) => m.set(u.id, u.full_name || u.email));
    return m;
  }, [users]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-taupe-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auditoría</h1>
            <p className="text-sm text-gray-500">Registro de las operaciones críticas (alta, edición y borrado).</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-xl p-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Usuario</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[12rem]">
            <option value="">Todos los usuarios</option>
            {(users || []).map((u) => (
              <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Tipo</label>
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
            {ENTITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Período</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={365}>Último año</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Cargando…</td></tr>
              ) : !logs || logs.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Sin registros para los filtros elegidos.</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{log.created_at ? formatDateTime(log.created_at) : '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800">
                      {log.user_id ? (userName.get(log.user_id) || log.user_email || `#${log.user_id}`) : (log.user_email || '-')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log.action)}`}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                      {log.entity_id ? <span className="text-gray-400 text-xs ml-1">#{log.entity_id}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{renderDetails(log.details)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Mostrando hasta 500 registros. Para ver más antiguos, acotá con los filtros.
      </p>
    </div>
  );
}
